// v2/components/PurchaseSummary.js
// Aylik alisveris ozeti — /api/v2/dashboard/ purchaseMonths
// Kaynak: ERP faturalari (tum kanallar), iadeler dusulmus, gunluk kumulatif
import React, { useState, useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { TrendingUp, TrendingDown, RotateCw } from 'lucide-react';
import {
  formatTRYShort, formatPercent, formatCompact, formatTRY, formatNumber,
} from '../utils/format';
import styles from './StatCards.module.css';

function ChartTip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipLabel}>{label}</div>
      <div>{formatTRY(payload[0].value)}</div>
    </div>
  );
}

export default function PurchaseSummary({
  months = [], year = null, loading = false, error = null, onRetry,
}) {
  const [monthId, setMonthId] = useState('');
  const month = useMemo(
    () => months.find((m) => m.id === monthId) || months[0],
    [months, monthId]
  );

  const baslik = (
    <div className={styles.cardHead}>
      <h2 className={styles.cardTitle}>Aylık Alışveriş Özeti</h2>
      {month && (
        <select
          className={styles.select}
          value={month.id}
          onChange={(e) => setMonthId(e.target.value)}
          aria-label="Ay seçimi"
        >
          {months.map((m) => (
            <option key={m.id} value={m.id}>{m.label}</option>
          ))}
        </select>
      )}
    </div>
  );

  if (!month) {
    return (
      <section className={styles.card} aria-label="Aylık Alışveriş Özeti">
        {baslik}
        <div className={styles.cardState}>
          {loading ? 'Yükleniyor…' : error ? (
            <>
              <span>{error}</span>
              {onRetry && (
                <button type="button" className={styles.retryBtn} onClick={onRetry}>
                  <RotateCw size={12} /> Tekrar dene
                </button>
              )}
            </>
          ) : 'Bu yıl fatura hareketiniz bulunmuyor.'}
        </div>
      </section>
    );
  }

  const degisim = month.changePct;
  const up = degisim !== null && degisim >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const kiyas = month.partial
    ? `geçen ayın ilk ${month.compareToDay} gününe göre`
    : 'önceki aya göre';

  return (
    <section className={styles.card} aria-label="Aylık Alışveriş Özeti">
      {baslik}

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div className={styles.bigValue}>{formatTRYShort(month.total)}</div>
        {degisim !== null && (
          <div className={`${styles.trend} ${up ? styles.up : styles.down}`} style={{ marginTop: 0 }}>
            <Icon size={14} />
            {formatPercent(Math.abs(degisim), 1)}
            <span className={styles.trendNote}>{kiyas}</span>
          </div>
        )}
      </div>

      <div className={styles.chartBox}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={month.series} margin={{ top: 8, right: 4, left: -14, bottom: 0 }}>
            <defs>
              <linearGradient id="purchaseFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2F80ED" stopOpacity={0.28} />
                <stop offset="100%" stopColor="#2F80ED" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#F1F5F9" vertical={false} />
            <XAxis
              dataKey="day" tickLine={false} axisLine={false}
              tick={{ fontSize: 11, fill: '#94A3B8' }} interval="preserveStartEnd" minTickGap={18}
            />
            <YAxis
              tickLine={false} axisLine={false} width={46}
              tick={{ fontSize: 11, fill: '#94A3B8' }} tickFormatter={formatCompact}
            />
            <Tooltip content={<ChartTip />} cursor={{ stroke: '#BFDBFE', strokeWidth: 1 }} />
            <Area
              type="monotone" dataKey="value"
              stroke="#2F80ED" strokeWidth={2} fill="url(#purchaseFill)"
              dot={false} activeDot={{ r: 4, fill: '#2F80ED', stroke: '#fff', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <p className={styles.cardNote}>
        Tüm kanallardaki faturalarınız, iadeler düşülmüştür.
        {year && (
          <> {year.year} toplamı: <b>{formatTRYShort(year.net)}</b> · {formatNumber(year.invoices)} fatura</>
        )}
      </p>
    </section>
  );
}
